(() => {
  if (customElements.get('osdtg-price-calculator')) return;
  // Quantity breaks (pickentory services/volume_tiers.py): 1-5, 6-11, 12-23, 24-49, 50-99, 100-249, 250+.
  const TIERS = [1, 6, 12, 24, 50, 100, 250];
  const QUOTE_URL = '/pages/quote-request';
  const range = (i) => (TIERS[i + 1] ? `${TIERS[i]}–${TIERS[i + 1] - 1}` : `${TIERS[i]}+`);
  const ERR = 'Pricing unavailable — check the customizer for an exact price';
  const money = (n) => '$' + Number(n).toFixed(2);
  // DTF is priced by design size per side (pickentory services/dtf_sizes.py): up to 11 × 16 in or up to 16 × 20 in.
  const SIZE_LABEL = { small: '11 × 16', large: '16 × 20' };
  // DTG light price = White / PFD only (pickentory store_pricing._LIGHT_GARMENT_NAMES); every other color is "colored".
  const LIGHT = new Set(['white', 'pfd white', 'pfd']);
  const isLight = (c) => LIGHT.has(String(c || '').toLowerCase().replace(/[/-]/g, ' ').split(/\s+/).filter(Boolean).join(' '));

  // One card per print method the product offers. Customer-chooses products show DTG and DTF side
  // by side (tap to pick); DTF has a design-size switch per printed side. The customer can flip
  // White / Colored here because DTG costs more on colored shirts.
  class OsdtgPriceCalculator extends HTMLElement {
    connectedCallback() {
      this.api = (this.dataset.api || '').replace(/\/$/, '');
      this.loc = 'front';
      // Print method: '' lets the server use the product default; methods come back with
      // the first price ('dtg' only, 'dtf' only, or both when the customer chooses).
      this.method = window.__pktPrintMethod || '';
      this.methods = null;
      this.sizes = { front: 'small', back: 'small' };
      this.color = this.dataset.color || '';
      this.lastColored = isLight(this.color) ? '' : this.color;
      this.cache = new Map();
      this.seq = 0;
      this.qtyEl = this.querySelector('[data-calc-qty]');
      this.qtyEl.addEventListener('input', () => this.schedule());
      this.querySelectorAll('[data-calc-loc]').forEach((b) =>
        b.addEventListener('click', () => {
          this.loc = b.dataset.calcLoc;
          this.querySelectorAll('[data-calc-loc]').forEach((x) => x.setAttribute('aria-checked', String(x === b)));
          this.paintSizes();
          this.onOptionsChange();
        })
      );
      this.querySelectorAll('[data-calc-shade]').forEach((b) =>
        b.addEventListener('click', () => {
          const white = b.dataset.calcShade === 'white';
          if (white === isLight(this.color)) return;
          this.applyColor(white ? 'White' : (this.lastColored || 'Black'));
        })
      );
      this.querySelectorAll('[data-calc-method]').forEach((b) =>
        b.addEventListener('click', () => this.pickMethod(b.dataset.calcMethod))
      );
      this.querySelectorAll('[data-calc-size]').forEach((b) =>
        b.addEventListener('click', () => {
          const side = b.dataset.calcSide || 'front';
          this.sizes[side] = b.dataset.calcSize;
          this.paintSizes();
          if (this.method !== 'dtf') this.pickMethod('dtf');
          this.onOptionsChange();
        })
      );
      // Stay in step with the DTG/DTF picker above "Customize this shirt" (customizer-embed.js).
      document.addEventListener('pkt:print-method', (e) => {
        if (e.detail && e.detail.source !== 'calc') this.setMethod(e.detail.method);
      });
      this.querySelector('[data-calc-breaks-btn]').addEventListener('click', () => this.toggleBreaks());
      // Dawn: listen for variant changes (pub/sub) and raw changes inside <variant-selects>.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, () => this.readColor());
        subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, () => setTimeout(() => this.readColor(), 0));
      }
      document.addEventListener('change', (e) => {
        if (e.target.closest && e.target.closest('variant-selects')) this.readColor();
      });
      // OSDTG product pages use the Pickentory swatch picker (snippets/buy-buttons.liquid),
      // which marks swatches with data-color-name and sets window.__pktSelectedColor.
      // customizer-embed.js redraws the swatches without data-color-name (its wrappers carry
      // data-pkt-color-name), so follow window.__pktSelectedColor after any swatch click.
      document.addEventListener('click', (e) => {
        const sw = e.target.closest && e.target.closest('[data-color-name], [data-pkt-color-name]');
        if (sw) setTimeout(() => this.setColor(window.__pktSelectedColor || sw.getAttribute('data-color-name')), 0);
      });
      this.readColor(true);
      this.paintShade();
      this.paintSizes();
      // Closed until the customer taps "How much for my order?"; nothing loads before that.
      this.opened = false;
      this.querySelector('[data-calc-open]').addEventListener('click', () => this.toggleOpen());
    }

    toggleOpen() {
      const btn = this.querySelector('[data-calc-open]');
      const body = this.querySelector('[data-calc-body]');
      const open = body.hidden;
      body.hidden = !open;
      btn.setAttribute('aria-expanded', String(open));
      this.classList.toggle('is-open', open);
      const t = this.querySelector('[data-calc-open-text]');
      if (t) t.textContent = open ? 'Hide' : 'Show prices';
      if (open && !this.opened) {
        this.opened = true;
        this.schedule(0);
      }
    }

    // Swatch picked on the page.
    setColor(val) {
      if (!val || val === this.color) return;
      this.applyColor(val);
    }

    applyColor(val, silent) {
      this.color = val;
      if (!isLight(val)) this.lastColored = val;
      this.paintShade();
      if (!silent) this.onOptionsChange();
    }

    paintShade() {
      const white = isLight(this.color);
      this.querySelectorAll('[data-calc-shade]').forEach((x) =>
        x.setAttribute('aria-checked', String((x.dataset.calcShade === 'white') === white)));
      this.querySelectorAll('[data-calc-shade-word]').forEach((x) => { x.textContent = white ? 'White' : 'colored'; });
    }

    readColor(silent) {
      // On Pickentory pages the hidden Dawn picker still points at the first variant, while the
      // swatches default to White; trust the snippet's data-color / the swatch picker instead.
      if (document.getElementById('pkt-customizer-block')) {
        if (!silent && window.__pktSelectedColor) this.setColor(window.__pktSelectedColor);
        return;
      }
      const opt = this.dataset.colorOption;
      const vs = opt && document.querySelector('variant-selects');
      if (!vs) return;
      const esc = CSS.escape(opt);
      let val = null;
      const checked = vs.querySelector(`input[name^="${esc}-"]:checked`);
      if (checked) val = checked.value;
      else {
        const sel = vs.querySelector(`select[name^="options[${esc}]"], select[name^="${esc}-"]`);
        if (sel) val = (sel.options[sel.selectedIndex]?.textContent || sel.value).trim();
      }
      if (val && val !== this.color) this.applyColor(val, silent);
    }

    // Front only: one size switch. Front + Back: a Front row and a Back row.
    paintSizes(prints) {
      const both = this.loc === 'both';
      const back = this.querySelector('[data-calc-size-row="back"]');
      if (back) back.hidden = !both;
      this.querySelectorAll('[data-calc-side-label]').forEach((x) => { x.hidden = !both; });
      this.querySelectorAll('[data-calc-size]').forEach((x) =>
        x.setAttribute('aria-checked', String(this.sizes[x.dataset.calcSide || 'front'] === x.dataset.calcSize)));
      const title = this.querySelector('[data-calc-size-title]');
      if (title) title.textContent = both ? 'Design size on each side' : 'Your design size';
      const help = this.querySelector('[data-calc-size-help]');
      if (!help) return;
      if (!both) help.textContent = 'Most chest prints fit 11×16. The designer measures yours.';
      else if (prints && prints.front != null && prints.back != null) {
        help.textContent = `Print: front ${money(prints.front)} + back ${money(prints.back)} a shirt. The designer measures each side.`;
      } else if (!prints) help.textContent = 'The designer measures each side.';
    }

    // Customer tapped a card: pick it here and tell the picker above "Customize this shirt".
    pickMethod(m) {
      if (!this.methods || this.methods.length < 2) return;
      this.setMethod(m);
      document.dispatchEvent(new CustomEvent('pkt:print-method', { detail: { method: this.method, source: 'calc' } }));
    }

    setMethod(m) {
      if (m !== 'dtg' && m !== 'dtf') return;
      if (this.methods && !this.methods.includes(m)) return;
      if (m === this.method) return;
      this.method = m;
      this.paintCards();
      const b = this.querySelector('[data-calc-breaks]');
      if (!b.hidden) this.loadBreaks();
      else b.dataset.stale = '1';
    }

    paintCards() {
      const both = !!(this.methods && this.methods.length > 1);
      this.classList.toggle('osdtg-calc--both', both);
      this.querySelectorAll('[data-calc-card]').forEach((card) => {
        const m = card.dataset.calcCard;
        card.hidden = !!this.methods && !this.methods.includes(m);
        const on = both && m === this.method;
        card.classList.toggle('is-selected', on);
        const btn = card.querySelector('[data-calc-method]');
        if (btn) {
          btn.setAttribute('aria-pressed', String(on));
          btn.disabled = !both;
        }
      });
    }

    // First price response says which methods the product offers.
    learnMethods(d) {
      if (this.methods || !Array.isArray(d.print_methods)) return;
      this.methods = d.print_methods.filter((m) => m === 'dtg' || m === 'dtf');
      if (!this.methods.length) this.methods = ['dtg'];
      if (!this.methods.includes(this.method)) this.method = d.print_method || this.methods[0];
      this.paintCards();
    }

    onOptionsChange() {
      const b = this.querySelector('[data-calc-breaks]');
      if (!this.opened) { b.dataset.stale = '1'; return; }
      if (!b.hidden) this.loadBreaks();
      else b.dataset.stale = '1';
      this.schedule();
    }

    qty() {
      const n = parseInt(this.qtyEl.value, 10);
      return Number.isFinite(n) && n >= 1 ? n : 1;
    }

    fetchPrice(qty, method = this.method, color = this.color) {
      const dtf = method === 'dtf';
      const body = JSON.stringify({
        product_id: String(this.dataset.productId),
        style_id: this.dataset.styleId,
        qty,
        color,
        size: 'L',
        print_location: this.loc,
        print_method: method,
        dtf_size: dtf ? this.sizes.front : '',
        dtf_back_size: dtf && this.loc === 'both' ? this.sizes.back : '',
      });
      if (!this.cache.has(body)) {
        const p = fetch(`${this.api}/stores/oversizedtg/price-estimate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
        })
          .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
          .then((d) => {
            if (typeof d.unit_price !== 'number') throw new Error('bad response');
            return d;
          });
        p.catch(() => this.cache.delete(body));
        this.cache.set(body, p);
      }
      return this.cache.get(body);
    }

    schedule(delay = 300) {
      if (!this.opened) return;
      clearTimeout(this.timer);
      this.classList.add('is-loading');
      this.timer = setTimeout(() => this.update(), delay);
    }

    async update() {
      const id = ++this.seq;
      const qty = this.qty();
      const err = this.querySelector('[data-calc-error]');
      const save = this.querySelector('[data-calc-save]');
      const badge = this.querySelector('[data-calc-badge]');
      const other = this.querySelector('[data-calc-other]');
      this.querySelectorAll('[data-calc-qty-label]').forEach((x) => { x.textContent = qty; });
      try {
        if (!this.methods) this.learnMethods(await this.fetchPrice(qty));
        if (id !== this.seq) return;
        const white = isLight(this.color);
        const otherColor = white ? (this.lastColored || 'Black') : 'White';
        const [rows, otherDtg] = await Promise.all([
          Promise.all(this.methods.map((m) => Promise.all([this.fetchPrice(qty, m), this.fetchPrice(1, m)]))),
          this.methods.includes('dtg') ? this.fetchPrice(qty, 'dtg', otherColor).catch(() => null) : null,
        ]);
        if (id !== this.seq) return;
        err.hidden = true;
        const unit = {};
        this.methods.forEach((m, i) => {
          const [cur] = rows[i];
          unit[m] = cur.unit_price;
          this.querySelector(`[data-calc-unit="${m}"]`).textContent = money(cur.unit_price);
          this.querySelector(`[data-calc-total="${m}"]`).textContent = money(cur.total ?? cur.unit_price * qty);
          if (m === 'dtf') this.paintSizes({ front: cur.front_print, back: cur.back_print });
        });
        // DTG: the other shirt shade's price, so White vs colored is never a surprise.
        if (other) other.textContent = otherDtg ? `${white ? 'Colored' : 'White'} shirts: ${money(otherDtg.unit_price)} / shirt` : '';
        // Both methods: DTF card says how much it saves. One method: savings vs a single shirt.
        const diff = unit.dtg != null && unit.dtf != null ? unit.dtg - unit.dtf : 0;
        badge.hidden = !(diff > 0.005);
        if (!badge.hidden) badge.textContent = `Save ${money(diff)}/shirt`;
        if (this.methods.length === 1) {
          const [cur, single] = rows[0];
          const pct = single.unit_price > 0 ? Math.round((1 - cur.unit_price / single.unit_price) * 100) : 0;
          save.hidden = !(qty > 1 && pct > 0);
          if (!save.hidden) save.textContent = `You save ${pct}% vs one shirt`;
        } else {
          save.hidden = true;
        }
        this.markActive();
      } catch (e) {
        if (id !== this.seq) return;
        this.querySelectorAll('[data-calc-unit], [data-calc-total]').forEach((x) => { x.textContent = '—'; });
        if (other) other.textContent = '';
        save.hidden = true;
        badge.hidden = true;
        err.hidden = false;
      } finally {
        if (id === this.seq) this.classList.remove('is-loading');
      }
    }

    markActive() {
      const q = this.qty();
      this.querySelectorAll('[data-calc-breaks] tr[data-min]').forEach((tr) => {
        const min = +tr.dataset.min, max = +(tr.dataset.max || Infinity);
        tr.classList.toggle('is-active', q >= min && q <= max);
      });
    }

    toggleBreaks() {
      const btn = this.querySelector('[data-calc-breaks-btn]');
      const box = this.querySelector('[data-calc-breaks]');
      const open = box.hidden;
      box.hidden = !open;
      btn.setAttribute('aria-expanded', String(open));
      btn.textContent = open ? 'Hide prices' : 'See all prices';
      if (open && (!box.dataset.loaded || box.dataset.stale)) this.loadBreaks();
    }

    // Every quantity break for the picked method (and DTF sizes); the customer's row is marked.
    async loadBreaks() {
      const box = this.querySelector('[data-calc-breaks]');
      box.dataset.stale = '';
      box.innerHTML = '<p class="osdtg-calc__muted">Loading prices…</p>';
      const m = this.method || (this.methods && this.methods[0]) || '';
      const results = await Promise.allSettled(TIERS.map((q) => this.fetchPrice(q, m)));
      if (results.every((r) => r.status === 'rejected')) {
        box.innerHTML = `<p class="osdtg-calc__error">${ERR}</p>`;
        return;
      }
      const shown = results.find((r) => r.status === 'fulfilled').value.print_method || m;
      const rows = TIERS.map((q, i) => {
        const r = results[i];
        const max = TIERS[i + 1] ? TIERS[i + 1] - 1 : '';
        return `<tr data-min="${q}" data-max="${max}"><td>${range(i)} shirts</td><td>${r.status === 'fulfilled' ? money(r.value.unit_price) : '—'}</td></tr>`;
      }).join('');
      const sizes = this.loc === 'both'
        ? `front ${SIZE_LABEL[this.sizes.front]} + back ${SIZE_LABEL[this.sizes.back]} in`
        : `design up to ${SIZE_LABEL[this.sizes.front]} in`;
      const label = (shown === 'dtf' ? `DTF, ${sizes}` : 'DTG') + ` on ${isLight(this.color) ? 'White' : 'colored'} shirts`;
      box.innerHTML = `<table class="osdtg-calc__table"><tbody>${rows}</tbody></table>` +
        `<p class="osdtg-calc__muted">${label} · 500+? <a href="${QUOTE_URL}">Get a quote</a></p>`;
      box.dataset.loaded = '1';
      this.markActive();
    }
  }
  customElements.define('osdtg-price-calculator', OsdtgPriceCalculator);
})();
